import _, {sortBy} from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';

import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {VisualWorkspaceTeammate} from '../../../../models/api/visualWorkspaceModel';
import {AppState} from '../../../../store/appState';
import {
  FindOnlineStatus,
  getFindOnlineStatus,
} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/teammatesSelectors';
import {WorkspaceDropReorderTeammateHandler} from '../../../../store/data/workspace/reorder/types';
import {
  computeVisualWorkspaceRecentlyViewedMap,
  TeammateModelMap,
  VisualWorkspaceRecentlyViewedMap,
} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {WorkspaceDragSourceTypesEnum} from '../dnd/workspaceDragDropTypes';
import {WorkspaceTeammateItemContainerBase} from '../workspaceTeammateItemContainerBase';
import {ToggleTeammateHandler} from './context/workspacesEditorContext';

/*
 * Constants.
 */

const teammatesIndentLevel = 0;

/*
 * Props.
 */

interface WorkspacesEditorTeammatesProps {
  isAlphabetized: boolean;
  onToggleTeammate: ToggleTeammateHandler;
  panelId: InboxesPanelId;
  onDropReorderTeammate: WorkspaceDropReorderTeammateHandler;
}

interface WorkspacesEditorTeammatesContainerComponentProps extends WorkspacesEditorTeammatesProps {
  teammates: Array<TeammateModel>;
  selectedTeammateIds: Array<number>;
  findOnlineStatus: FindOnlineStatus;
  recentlyViewedMap: VisualWorkspaceRecentlyViewedMap;
}

interface WorkspacesEditorTeammatesContainerProps extends WorkspacesEditorTeammatesProps {
  visualWorkspaceTeammates: ReadonlyArray<VisualWorkspaceTeammate>;
  teammateModelMap: TeammateModelMap;
}

/*
 * Selectors.
 */

function getVisualWorkspaceTeammates(_state: AppState, props: WorkspacesEditorTeammatesContainerProps) {
  return props.visualWorkspaceTeammates;
}

function getSelectedTeammateIds(_state: AppState, props: WorkspacesEditorTeammatesContainerProps) {
  return _(props.visualWorkspaceTeammates)
    .filter((teammate) => teammate.isSelected)
    .map((teammate) => teammate.id)
    .value();
}

function getTeammateModelMap(_state: AppState, props: WorkspacesEditorTeammatesContainerProps) {
  return props.teammateModelMap;
}

function makeGetTeammates() {
  return createSelector(
    [getFindTeammate, getVisualWorkspaceTeammates, getTeammateModelMap],
    (findTeammate, visualWorkspaceTeammates, teammateModelMap) => {
      const teammates = _(visualWorkspaceTeammates)
        .map((visualWorkspaceTeammate) => visualWorkspaceTeammate.id)
        .map((id) => findTeammate(id) ?? teammateModelMap[id])
        .compact()
        .value();
      return teammates;
    },
  );
}

const getRecentlyViewedMap = createSelector([getVisualWorkspaceTeammates], (visualWorkspaceTeammates) =>
  computeVisualWorkspaceRecentlyViewedMap(visualWorkspaceTeammates),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<WorkspacesEditorTeammatesContainerProps>()(() => ({
  teammates: makeGetTeammates(),
  selectedTeammateIds: getSelectedTeammateIds,
  findOnlineStatus: getFindOnlineStatus,
  recentlyViewedMap: getRecentlyViewedMap,
}));

export const WorkspacesEditorTeammatesContainerComponent: FC<
  WorkspacesEditorTeammatesContainerComponentProps
> = (props) => {
  const {
    teammates,
    findOnlineStatus,
    isAlphabetized,
    selectedTeammateIds,
    onDropReorderTeammate,
    recentlyViewedMap,
  } = props;
  const sortedTeammates = isAlphabetized
    ? sortBy(teammates, (t) => (t.givenName + t.familyName).toLowerCase())
    : teammates;
  const lastIndex = sortedTeammates.length - 1;
  return (
    <>
      {sortedTeammates.map((t, index) => (
        <WorkspaceTeammateItemContainerBase
          key={t.id}
          indentLevel={teammatesIndentLevel}
          panelId={props.panelId}
          teammate={t}
          isChecked={selectedTeammateIds.includes(t.id)}
          isRecentlyViewed={recentlyViewedMap[t.id]}
          onClick={() => props.onToggleTeammate(t.id, false)}
          canBeHidden={false}
          shouldHideMoreMenu
          isEditingWorkspace
          shouldAlphabetizeWorkspace={isAlphabetized}
          onlineStatus={findOnlineStatus(t)}
          workspaceDragItem={{
            type: WorkspaceDragSourceTypesEnum.ASSIGNED_TEAMMATE,
            id: t.id,
            isLast: index === lastIndex,
          }}
          onDropReorderTeammate={onDropReorderTeammate}
        />
      ))}
    </>
  );
};

export const WorkspacesEditorTeammatesContainer = connect(mapStateToProps)(
  WorkspacesEditorTeammatesContainerComponent,
);
